import { ApiQuery, SortDirections, SortQuery } from "~/modules/filters/types";
import {
    ColumnMetadata,
    ColumnTypes,
    DataGrid,
    DataGridRow,
    RowSizes,
} from "~/components/DataGrid";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import {
    LeagueTableRowData,
    LeagueTableTab,
} from "~/modules/league_tables/types";
import { DataGridPercentChangeValue } from "~/components/DataGrid/DataGridPercentChangeValue";
import { roundN } from "~/utils/functions/roundN";
import { cn } from "~/lib/utils";
import { RWA_COIN_ICON } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import { Link } from "~/components/Link";
import { Anchor } from "~/components/Anchor";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { useState } from "react";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { PercentageBar } from "~/components/PercentageBar";

// // // //

enum LeagueTableColumns {
    rowIndex = "rowIndex",
    group = "group",
    value = "value",
    pct_change = "pct_change",
    pct_transferable = "pct_distributed",
    market_share = "market_share",
    asset_count = "asset_count",
}

const PERCENT_CHANGE_COLUMN: ColumnMetadata<
    LeagueTableRowData,
    LeagueTableColumns
> = {
    slug: LeagueTableColumns.pct_change,
    sortsOn: LeagueTableColumns.pct_change,
    label: "30D%",
    type: ColumnTypes.percentChange,
    width: 80,
    loginRequired: true,
    render: (a) => {
        if (a.value_7d_ago === -1) {
            return null;
        }

        if (a.value_7d_ago === 0) {
            return null;
        }

        return <DataGridPercentChangeValue value={a.value_7d_change} />;
    },
};

const COLUMNS: ColumnMetadata<LeagueTableRowData, LeagueTableColumns>[] = [
    {
        slug: LeagueTableColumns.rowIndex,
        label: "#",
        type: ColumnTypes.number,
        width: 50,
        maxWidth: 50,
        render: (a) => {
            return a.rowIndex + 1;
        },
    },
    // ENHANCEMENT - it might be nice to show an icon here if we can update the API to support that?
    {
        slug: LeagueTableColumns.group,
        sortsOn: LeagueTableColumns.group,
        label: "Group", // NOTE - this label changes based on the tab that's selected, see below
        type: ColumnTypes.string,
        width: 140,
        render: (a) => {
            const linkTo = a.group.linkTo;
            if (!a.group.icon_url) {
                if (!linkTo) {
                    return a.group.name;
                }

                if (linkTo.openInNewTab) {
                    return (
                        <div className="flex items-center relative z-10">
                            <Anchor
                                href={linkTo.href}
                                variant="default"
                                className="dg-overflow"
                                analyticsLabel={linkTo.analyticsLabel}
                                openInNewTab
                            >
                                {a.group.name}
                            </Anchor>
                        </div>
                    );
                }

                return (
                    <div className="flex items-center relative z-10">
                        <Link
                            href={linkTo.href}
                            variant="default"
                            className="dg-overflow"
                            analyticsLabel={linkTo.analyticsLabel}
                        >
                            {a.group.name}
                        </Link>
                    </div>
                );
            }

            if (!linkTo) {
                return (
                    <div className="flex items-center gap-2">
                        <ImageLoader
                            src={a.group.icon_url as string}
                            className={cn(
                                "rounded-full relative inline-block h-4 w-4 min-w-4",
                            )}
                            alt={`${a.group.name} Logo`}
                            fallbackSrc={RWA_COIN_ICON}
                            fallbackClassName="bg-white"
                        />
                        <span className="dg-overflow">{a.group.name}</span>
                    </div>
                );
            }

            if (linkTo.openInNewTab) {
                return (
                    <Anchor
                        href={linkTo.href}
                        variant="default"
                        className="relative flex items-center gap-2"
                        analyticsLabel={linkTo.analyticsLabel}
                        openInNewTab
                    >
                        <ImageLoader
                            src={a.group.icon_url as string}
                            className={cn(
                                "rounded-full relative inline-block h-4 w-4 min-w-4",
                            )}
                            alt={`${a.group.name} Logo`}
                            fallbackSrc={RWA_COIN_ICON}
                            fallbackClassName="bg-white"
                        />
                        <span className="dg-overflow">{a.group.name}</span>
                    </Anchor>
                );
            }

            return (
                <Link
                    href={linkTo.href}
                    variant="default"
                    className="relative flex items-center gap-2"
                    analyticsLabel={linkTo.analyticsLabel}
                >
                    <ImageLoader
                        src={a.group.icon_url as string}
                        className={cn(
                            "rounded-full relative inline-block h-4 w-4 min-w-4",
                        )}
                        alt={`${a.group.name} Logo`}
                        fallbackSrc={RWA_COIN_ICON}
                        fallbackClassName="bg-white"
                    />
                    <span className="dg-overflow">{a.group.name}</span>
                </Link>
            );
        },
    },
    {
        slug: LeagueTableColumns.value,
        sortsOn: LeagueTableColumns.value,
        label: "Market Cap",
        type: ColumnTypes.number,
        // loginRequired: true,
        width: 100,
        render: (a) => {
            return formatLeagueTableCurrencyValue(a.value);
        },
    },
    PERCENT_CHANGE_COLUMN,
    {
        slug: LeagueTableColumns.market_share,
        sortsOn: LeagueTableColumns.market_share,
        label: "Market Share",
        type: ColumnTypes.number,
        loginRequired: true,
        width: 110,
        render: (a) => {
            return (
                <DataGridNumberPercentChangeComboCell
                    value={a.market_share_pct}
                    pct_change_value={a.market_share_pct_30d_change}
                    pct_change_interval="30d"
                    type="percent"
                />
            );
        },
    },
];

function formatLeagueTableCurrencyValue(value: number) {
    if (value < 100_000) {
        return `$` + roundN(value, 1).toLocaleString();
    }

    return "$" + formatCurrencyValueCompact(value);
}

const ASSET_COUNT_COLUMN: ColumnMetadata<
    LeagueTableRowData,
    LeagueTableColumns
> = {
    slug: LeagueTableColumns.asset_count,
    sortsOn: LeagueTableColumns.asset_count,
    label: "RWA Count",
    // tooltip: "Number of issued tokens across all assets and networks",
    // loginRequired: true,
    type: ColumnTypes.number,
    width: 65,
    cellValue: (a) => {
        return a.asset_count;
    },
};

const PERCENT_DISTRIBUTED_COLUMN: ColumnMetadata<
    LeagueTableRowData,
    LeagueTableColumns
> = {
    slug: LeagueTableColumns.pct_transferable,
    sortsOn: LeagueTableColumns.pct_transferable,
    label: "% Distributed",
    type: ColumnTypes.number,
    width: 120,
    render: (a) => {
        return <PercentageBar percentage={a.pct_transferable || 0} />;
    },
};

// // // //

/**
 * LEAGUE_TABLE_SORT_MAP
 * Map each AssetColumns to a key used to in-memory sort the array of items using `sortItemsByNestedProperty`
 */
const LEAGUE_TABLE_SORT_MAP: Record<LeagueTableColumns, string> = {
    [LeagueTableColumns.rowIndex]: "rowIndex",
    [LeagueTableColumns.group]: "group.name",
    [LeagueTableColumns.value]: "value",
    [LeagueTableColumns.pct_change]: "value_7d_change",
    [LeagueTableColumns.market_share]: "market_share_pct",
    [LeagueTableColumns.asset_count]: "asset_count",
    [LeagueTableColumns.pct_transferable]: "pct_transferable",
};

// // // //

// ENHANCEMENT - update this to accept an optional ApiQuery object for the default query value
function useInMemoryLeagueTableSort(props: {
    rows: LeagueTableRowData[];
    sort?: SortQuery;
    onChangeQuery?: () => void;
}) {
    const {
        rows,
        sort = {
            field: LeagueTableColumns.value,
            direction: SortDirections.desc,
        },
        onChangeQuery,
    } = props;

    const [query, setQuery] = useState<ApiQuery>({
        sort,
        pagination: {
            page: 1,
            perPage: 25,
        },
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: LEAGUE_TABLE_SORT_MAP[query.sort.field as LeagueTableColumns],
        direction: query.sort.direction,
        nullsLast: true,
    });

    function setQueryWithDefaultSort(q: ApiQuery) {
        const useDefaultSort = q.sort.field === SortDirections.none;

        setQuery({
            ...q,
            sort: {
                ...q.sort,
                field: useDefaultSort ? sort.field : q.sort.field,
                direction: useDefaultSort ? sort.direction : q.sort.direction,
            },
        });

        // Notify the parent component that the query has changed
        if (onChangeQuery) {
            onChangeQuery();
        }
    }

    return { query, setQuery: setQueryWithDefaultSort, sortedItems };
}

// // // //

export function LeagueTableDataGrid(props: {
    tab: LeagueTableTab;
    analyticsTableName: string;
    maxRows: number;
    hideRankColumn?: boolean;
    onChangeQuery?: () => void;
}) {
    const {
        tab,
        analyticsTableName,
        maxRows,
        hideRankColumn = false,
        onChangeQuery,
    } = props;

    const { query, setQuery, sortedItems } = useInMemoryLeagueTableSort({
        rows: tab.data.rows,
        sort: {
            field: LeagueTableColumns.value,
            direction: SortDirections.desc,
        },
        onChangeQuery,
    });

    // Check if any row has a non-null assetCount
    const hasAssetCounts = tab.data.rows.some((row) => row.asset_count != null);

    // Check if any row has a non-null pct_distributed
    const hasPercentTransferable = tab.data.rows.some(
        (row) => row.pct_transferable != null,
    );
    // Update the columns so the group column corresponds to the selected tab
    const dynamicColumns: ColumnMetadata<
        LeagueTableRowData,
        LeagueTableColumns
    >[] = [
        {
            ...COLUMNS[0],
            sortsOn: COLUMNS[0].sortsOn,
        },
        {
            ...COLUMNS[1],
            label: tab.data.groupColumnLabel,
            sortsOn: COLUMNS[1].sortsOn,
        },
        {
            ...COLUMNS[2],
            label: tab.data.valueColumnLabel,
            sortsOn: COLUMNS[2].sortsOn,
        },
        {
            ...COLUMNS[3],
            sortsOn: COLUMNS[3].sortsOn,
        },
        {
            ...COLUMNS[4],
            sortsOn: COLUMNS[4].sortsOn,
        },
    ];

    if (hasAssetCounts) {
        dynamicColumns.splice(2, 0, ASSET_COUNT_COLUMN);
    }

    if (hasPercentTransferable) {
        dynamicColumns[4] = PERCENT_DISTRIBUTED_COLUMN;
        dynamicColumns[5] = PERCENT_CHANGE_COLUMN;
    }

    // Filter out rank column if hideRankColumn is true
    const columns = hideRankColumn
        ? dynamicColumns.filter((c) => c.slug !== "rowIndex")
        : dynamicColumns;

    return (
        <DataGrid
            query={query}
            loading={false}
            columns={columns}
            paginationState={{
                page: 1,
                pageCount: 1,
                perPage: maxRows,
                resultCount: sortedItems.slice(0, maxRows).length,
            }}
            rows={sortedItems.slice(0, maxRows)}
            rowKey="group.slug"
            analyticsTableName={analyticsTableName}
            includeDummyRows
            size={RowSizes.extraSmall}
            disablePagination
            enableScrollbar={false}
            flush
            watermark
            onChangeQuery={setQuery}
        >
            {({ row, rowIndex, scrollLeft }) => (
                <DataGridRow
                    key={row.group.id}
                    row={{ ...row, rowIndex }}
                    columns={columns}
                    size={RowSizes.small}
                    scrollLeft={scrollLeft}
                    striped={rowIndex % 2 === 1}
                />
            )}
        </DataGrid>
    );
}
